import { t } from 'i18next';
import { mapValues } from 'lodash';
import { useMutation } from 'react-query';

import { bomComponentService } from 'bom/services';
import { IAddComponentsToJobPayload, IUpsertBomComponentPayload } from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { IInfiniteQueryOptions } from 'core/types';
import { jobPartService } from 'jobs/services';
import { IJobPart, IJobPartListRequestPayload, IUpsertJobPartPayload } from 'jobs/types';
import { partService } from 'parts/services';

export const useGetJobParts = (
  requestPayload?: IJobPartListRequestPayload,
  options: IInfiniteQueryOptions<IJobPart> = {}
) => useInfiniteQuery<IJobPart>(jobPartService.getJobParts, 'jobParts', requestPayload, options);

export const useJobPartActions = ({
  notificationPosition = NotificationPosition.FORM,
  selectionCount = 0,
  isEdit = false,
}: {
  notificationPosition?: NotificationPosition;
  selectionCount?: number;
  isEdit?: boolean;
} = {}) => ({
  ...useUpsertJobPart(notificationPosition),
  ...useValidatePart(),
  ...useCalculatePrices(),
  ...useUpsertJobPartComponent(isEdit),
  ...useRemoveJobPartComponent(),
  ...useAddPartsToJob(selectionCount),
  ...useReorderJobParts(),
});

const useUpsertJobPart = (notificationPosition = NotificationPosition.FORM) => {
  const {
    shouldUpdateUnverifiedPart,
    shouldChangeExistingJobPart,
    shouldUpsertBomComponent,
    handleUnverifiedPartUpdate,
    handleJobPartChange,
    handleBomComponentUpsert,
  } = jobPartUpsertHelper();

  const { errors } = useHandleErrors({
    success: ({ bomPart }) =>
      bomPart.bomPartId || bomPart.componentId || bomPart.partId
        ? { message: t('messageJobPartLineUpdateSuccess') }
        : { message: t('messageJobPartLineCreateSuccess') },
    loading: ({ bomPart }) =>
      bomPart.bomPartId || bomPart.componentId || bomPart.partId
        ? { message: t('messageJobPartLineUpdateInProgress') }
        : { message: t('messageJobPartLineCreateInProgress') },
    error: ({ bomPart }) =>
      bomPart.bomPartId || bomPart.componentId || bomPart.partId
        ? { title: t('errorTitleJobPartLineUpdate') }
        : { title: t('errorTitleJobPartLineCreate') },
    position: notificationPosition,
    invalidateQueries: ['jobParts', 'elements'],
  });

  const { mutateAsync } = useMutation(
    (data: IUpsertJobPartPayload) => {
      if (shouldUpdateUnverifiedPart(data)) {
        return handleUnverifiedPartUpdate(data);
      }

      if (shouldUpsertBomComponent(data)) {
        return handleBomComponentUpsert(data);
      }

      if (shouldChangeExistingJobPart(data)) {
        return handleJobPartChange(data);
      }

      return jobPartService.upsertJobPart(data);
    },
    {
      ...errors,
    }
  );

  return { upsertJobPart: mutateAsync };
};

const useValidatePart = () => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messageJobPartLineValidateInProgress') }),
    error: () => ({ title: t('errorTitleJobPartLineValidate') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobPartService.validatePart, {
    ...errors,
  });

  return { validatePart: mutateAsync };
};

const useCalculatePrices = () => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messageJobPartLineCalculatePricesInProgress') }),
    error: () => ({ title: t('errorTitleJobPartLineCalculatePrices') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobPartService.calculatePrices, {
    ...errors,
  });

  return { calculatePrices: mutateAsync };
};

const useUpsertJobPartComponent = (isEdit = false) => {
  const { errors } = useHandleErrors({
    success: () =>
      isEdit
        ? { message: t('messageJobPartComponentUpdateSuccess') }
        : { message: t('messageJobPartComponentAddSuccess') },
    loading: () =>
      isEdit
        ? { message: t('messageJobPartComponentUpdateInProgress') }
        : { message: t('messageJobPartComponentAddInProgress') },
    error: () =>
      isEdit
        ? { title: t('errorTitleJobPartComponentUpdate') }
        : { title: t('errorTitleJobPartComponentAdd') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobPartService.upsertJobPart, { ...errors });

  return { upsertJobPartComponent: mutateAsync };
};

const useRemoveJobPartComponent = () => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageJobPartComponentRemoveSuccess') }),
    loading: () => ({ message: t('messageJobPartComponentRemoveInProgress') }),
    error: () => ({ title: t('errorTitleJobPartComponentRemove') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(bomComponentService.removeComponentFromPart, {
    ...errors,
  });

  return { removeJobPartComponent: mutateAsync };
};

const useAddPartsToJob = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messageJobPartsAddToJobInProgress', { count: selectionCount }) }),
    error: () => ({ title: t('errorTitleJobPartsAddToJob', { count: selectionCount }) }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (data: IAddComponentsToJobPayload) => bomComponentService.addComponentsToJob(data),
    {
      ...errors,
    }
  );

  return { addPartsToJob: mutateAsync };
};

const useReorderJobParts = () => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageLinesReorderSuccess') }),
    loading: () => ({ message: t('messageLinesReorderInProgress') }),
    error: () => ({ title: t('errorTitleLinesReorder') }),
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(jobPartService.reorderJobParts, {
    ...errors,
  });

  return { reorderJobParts: mutateAsync };
};

const jobPartUpsertHelper = () => {
  const IDENTITY_FIELDS = ['id', 'bomId', 'bomPartId', 'partId', 'componentId', 'elementId'];
  const PART_FIELDS = ['description', 'uom'];

  const partToJobPartFieldsMap = {
    description: 'description',
    descriptionLong: 'description',
    uom: 'uom',
  };

  const shouldUpdateUnverifiedPart = (data: IUpsertJobPartPayload) => {
    const fieldsWithoutIdentity = Object.keys(data.bomPart).filter(
      (field) => !IDENTITY_FIELDS.includes(field)
    );

    return (
      data.bomPart.partId &&
      fieldsWithoutIdentity.length > 0 &&
      fieldsWithoutIdentity.every((field) => PART_FIELDS.includes(field))
    );
  };

  const shouldUpsertBomComponent = (data: IUpsertJobPartPayload) => !data.bomPart.part;

  const shouldChangeExistingJobPart = (data: IUpsertJobPartPayload) => !!data.bomPart.bomPartId;

  const createUnverifiedPartPayload = (data: IUpsertJobPartPayload) => ({
    part: {
      id: data?.bomPart?.partId,
      ...mapValues(partToJobPartFieldsMap, (value) => data.bomPart?.[value]),
    },
  });

  const createBomComponentUpsertPayload = (
    data: IUpsertJobPartPayload
  ): IUpsertBomComponentPayload => ({
    component: {
      bomId: data.bomPart.bomId,
      id: data.bomPart.componentId,
      manufacturerId: data.bomPart?.manufacturerId,
      name: data.bomPart?.description,
      uom: data.bomPart?.uom,
      ...(data.bomPart.quantity && { quantity: data.bomPart.quantity }),
      ...(data.bomPart.addToElements && {
        addToElements: data.bomPart.addToElements,
      }),
      ...(data.bomPart.updateElements && {
        updateElements: data.bomPart.updateElements,
      }),
      ...(data.bomPart.removeFromElements && {
        removeFromElements: data.bomPart.removeFromElements,
      }),
    },
  });

  const createJobPartChangePayload = (data: IUpsertJobPartPayload): IUpsertJobPartPayload => ({
    bomPart: {
      ...data.bomPart,
      id: data.bomPart.bomPartId,
    },
  });

  const handleUnverifiedPartUpdate = (data: IUpsertJobPartPayload) =>
    partService.upsertUnverifiedPart(createUnverifiedPartPayload(data));

  const handleBomComponentUpsert = (data: IUpsertJobPartPayload) =>
    bomComponentService.upsertBomComponent(createBomComponentUpsertPayload(data));

  const handleJobPartChange = (data: IUpsertJobPartPayload) =>
    jobPartService.changeJobPart(createJobPartChangePayload(data));

  return {
    shouldUpdateUnverifiedPart,
    shouldUpsertBomComponent,
    shouldChangeExistingJobPart,
    handleUnverifiedPartUpdate,
    handleBomComponentUpsert,
    handleJobPartChange,
  };
};
